import {createRef, ReactElement, useState} from "react";
import {ModalHeader, ModalBody, ModalFooter} from "@/widgets/ModalParts";
import {Button} from "@/components/buttons/Button";
import {Modal} from "@/widgets/Modal";
import {useSelector} from "react-redux";
import {IStore} from "../../redux/defaultStore";
import Cropper, {ReactCropperElement} from "react-cropper";
import "cropperjs/dist/cropper.css";
import Toast from "../Toast";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import {sendMessageToOW} from "@/hooks/useOverwolfInterop";
import {useUpdateProfilePicture} from "@/hooks/useUpdateProfilePicture";

interface Props {
    image: string;
    isOpen: boolean;
    onClose: () => Promise<void>;
}

function EditImageModal(props: Props): ReactElement {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const cropperRef = createRef<ReactCropperElement>();
    const [
        showToast,
        setShowToast,
    ] = useState<boolean>(false);

    const {data: currentUser, refetch: refetchUserProfile} = useGetUserProfile(fullToken);
    const {mutateAsync: updateUsersUpdateProfilePicture} = useUpdateProfilePicture();

    async function confirmCrop(): Promise<void> {
        if (typeof cropperRef.current?.cropper !== "undefined") {
            const cropData: Blob = await new Promise(resolve => {
                cropperRef.current?.cropper.getCroppedCanvas().toBlob(resolve);
            });

            await updateUsersUpdateProfilePicture({
                asset: cropData,
                name: `avatar_${currentUser.user.id}`,
                description: `Avatar for ${currentUser.user.id}`,
            });

            const {data: userRes} = await refetchUserProfile();
            // Tell Overwolf to update the user data
            sendMessageToOW({
                type: "de:update-user",
                payload: {
                    nickname: userRes.user.nickname,
                    gravatar: userRes.user.gravatar,
                },
            });
            await props.onClose();
            setShowToast(true);
        }
    }

    function handleToastDismissal() {
        setShowToast(false);
    }

    return (
        <>
            <Toast
                message="Your changes were saved!"
                isOpen={showToast}
                onDismiss={handleToastDismissal}
            />
            <Modal
                isOpen={props.isOpen}
                toggle={props.onClose}
                contentClassName="edit-image-modal"
                size={"lg"}
                usePortal={true}
            >
                <ModalHeader
                    title="Position and Resize your Avatar"
                    toggle={props.onClose}
                />

                <ModalBody className="edit-image-modal_body">
                    <Cropper
                        ref={cropperRef}
                        className="edit-image-modal_cropper"
                        aspectRatio={1}
                        initialAspectRatio={1}
                        src={props.image}
                        viewMode={1}
                        minCropBoxHeight={100}
                        minCropBoxWidth={100}
                        background={false}
                        responsive={true}
                        autoCropArea={0.8}
                        checkOrientation={true}
                        guides={true}
                        dragMode="move"
                        cropBoxMovable={true}
                        cropBoxResizable={true}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button variant="primary"
                        onClick={confirmCrop}
                    >
                        Confirm
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default EditImageModal;
